import { useMemo, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RoleBanner } from '../../components/brand/index.js';
import { useOptionalAuth } from '../../lib/auth/AuthProvider.js';
import { Button } from '../../components/primitives/index.js';
import { Icon, machineMotif } from '../../components/icons/index.js';
import { BlockedState, EmptyState, ErrorState } from '../../components/feedback/index.js';
import { Pagination } from '../../components/data/index.js';
import {
  EquipmentFilters,
  EquipmentGrid,
  EquipmentGridSkeleton,
  type FilterValues,
} from '../../components/discovery/index.js';
import { fetchAttributes, fetchModels, fetchTaxonomy } from '../../lib/api/queries/catalog.js';
import {
  searchEquipment,
  type AttributeFilter,
  type SearchRequest,
} from '../../lib/api/queries/equipment.js';

/**
 * `/equipment` — P-Q011 with the filter contract from P-Q004/005/007.
 *
 * All filter state lives in the URL, so a result set is a link: a site manager
 * can send "these excavators, free on these dates" to a colleague and they see
 * the same page. Paging is by opaque cursor (§9.1), which orders results but
 * cannot address a page by number, so the controls are Next and Back rather
 * than a page list — offering "page 7" would imply an index the API has not
 * built.
 *
 * **The order of this page is the point.** It used to open on a filter rail and
 * a sentence about what the search cannot do, so the first thing the product
 * said to a customer was a limitation. It now opens on the task, then on the
 * catalogue's own categories as something to BROWSE, then on how many machines
 * there are, then on the machines. Filters follow the results in the document
 * rather than preceding them, and sit beside them only once there is a screen
 * wide enough for both. Nothing about what is fetched changed.
 */
const PAGE_SIZE = 20;

function readFilters(params: URLSearchParams): FilterValues {
  const attributes: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    if (key.startsWith('attr.') && value !== '') attributes[key.slice(5)] = value;
  }
  return {
    taxonomy: params.get('category') ?? '',
    manufacturer: params.get('manufacturer') ?? '',
    model: params.get('model') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    attributes,
  };
}

function writeFilters(values: FilterValues): URLSearchParams {
  const params = new URLSearchParams();
  if (values.taxonomy !== '') params.set('category', values.taxonomy);
  if (values.manufacturer !== '') params.set('manufacturer', values.manufacturer);
  if (values.model !== '') params.set('model', values.model);
  if (values.from !== '') params.set('from', values.from);
  if (values.to !== '') params.set('to', values.to);
  for (const [code, value] of Object.entries(values.attributes)) {
    if (value !== '') params.set(`attr.${code}`, value);
  }
  return params;
}

/** A date input gives `YYYY-MM-DD`; P-Q011 wants an offset date-time. */
function toInstant(date: string, endOfDay: boolean): string {
  return `${date}T${endOfDay ? '23:59:59' : '00:00:00'}.000Z`;
}

function buildRequest(values: FilterValues, cursor: string | null): SearchRequest {
  const attributes: AttributeFilter[] = Object.entries(values.attributes)
    .filter(([, value]) => value !== '')
    .map(([code, value]) => ({ code, op: 'eq', value }));

  return {
    ...(values.taxonomy === '' ? {} : { taxonomy_node_code: values.taxonomy }),
    ...(values.manufacturer === '' ? {} : { manufacturer_id: values.manufacturer }),
    ...(values.model === '' ? {} : { model_id: values.model }),
    ...(attributes.length === 0 ? {} : { attributes }),
    ...(values.from !== '' && values.to !== ''
      ? { period: { start: toInstant(values.from, false), end: toInstant(values.to, true) } }
      : {}),
    ...(cursor === null ? {} : { cursor }),
    limit: PAGE_SIZE,
  };
}

export function EquipmentSearchPage(): ReactElement {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  /*
    `useOptionalAuth`, not `useAuth`. This page is reachable without an account
    and must render without a provider: the seat word is PRESENTATION, and the
    fallback — the product's own context rather than a seat — is the correct
    answer for a visitor, not a degraded one. Anything that GATES on a
    capability keeps using `useAuth`, so a missing provider stays a crash there
    rather than becoming a leak.
  */
  const session = useOptionalAuth();

  const values = useMemo(() => readFilters(params), [params]);
  const cursor = params.get('cursor');
  // Dates count once: "from and to" is one constraint the reader set, not two.
  const activeFilterCount =
    (values.taxonomy === '' ? 0 : 1) +
    (values.manufacturer === '' ? 0 : 1) +
    (values.model === '' ? 0 : 1) +
    (values.from === '' && values.to === '' ? 0 : 1) +
    Object.values(values.attributes).filter((v) => v !== '').length;
  const hasFilters = activeFilterCount > 0;

  const taxonomy = useQuery({ queryKey: ['taxonomy'], queryFn: () => fetchTaxonomy() });
  // Top level only. A rail of every equipment type is a filter list wearing a
  // different shape; a category is what somebody browses by.
  const categories = (taxonomy.data ?? []).filter((node) => node.level === 'category').slice(0, 10);
  const models = useQuery({ queryKey: ['models'], queryFn: () => fetchModels({ limit: 200 }) });
  const attributes = useQuery({
    queryKey: ['attributes'],
    queryFn: () => fetchAttributes('both'),
  });

  const request = buildRequest(values, cursor);
  const results = useQuery({
    queryKey: ['equipment-search', request],
    queryFn: () => searchEquipment(request),
  });

  const modelsById = useMemo(
    () => new Map((models.data ?? []).map((model) => [model.id, model])),
    [models.data],
  );
  // Every node, not just the top level: a model's `taxonomy_node_id` usually
  // points at the equipment TYPE, which is the more precise thing to print.
  const taxonomyById = useMemo(
    () => new Map((taxonomy.data ?? []).map((node) => [node.id, node])),
    [taxonomy.data],
  );

  function applyFilters(next: FilterValues): void {
    // Any filter change invalidates the cursor: it encodes the filter hash the
    // backend issued it under, and replaying it would be rejected.
    setParams(writeFilters(next), { replace: false });
  }

  function goToNextPage(next: string): void {
    const query = writeFilters(values);
    query.set('cursor', next);
    setParams(query);
  }

  const searchedWithDates = values.from !== '' && values.to !== '';
  const selectedCategory = categories.find((node) => node.code === values.taxonomy);

  return (
    <div className="search-page">
      {/*
        The masthead. Three lines, and the third is the only one that changes:
        who this is for, what the screen is for, and what is currently being
        looked at.
      */}
      <header className="search-page__masthead enter">
        {/*
          A seat word only once a seat has been taken. Browsing needs no
          account — the landing page says so in as many words — so telling a
          signed-out visitor they are "Renting" contradicts the one promise
          this page is built on.
        */}
        <RoleBanner role={(session?.account ?? null) === null ? 'public' : 'customer'} />
        <h1 className="search-page__title">Find the right machine</h1>
        <p className="search-page__lede">
          Published plant you can request terms on — browsable by category and searchable by
          manufacturer, model and specification.
        </p>
      </header>

      {categories.length === 0 ? null : (
        <nav className="search-page__categories" aria-label="Browse by category">
          <ul className="category-rail stagger">
            {categories.map((node) => {
              const selected = values.taxonomy === node.code;
              return (
                <li key={node.code}>
                  <button
                    type="button"
                    className={`category-tile${selected ? ' category-tile--selected' : ''} interactive`}
                    aria-pressed={selected}
                    onClick={() => applyFilters({ ...values, taxonomy: selected ? '' : node.code })}
                  >
                    <span className="category-tile__glyph" aria-hidden="true">
                      <Icon name={machineMotif(node.name)} size="md" />
                    </span>
                    <span className="category-tile__label">{node.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      )}

      <div className="search-page__layout">
        <section
          className="search-page__results"
          aria-label="Results"
          aria-busy={results.isLoading}
        >
          {/*
            The count is a headline, not a caption. "How many machines are
            there" is the question a marketplace is asked first, and it was
            being answered in 13px grey beneath a filter rail.
          */}
          <div className="result-head">
            <p className="result-head__count" role="status" aria-live="polite">
              {results.isLoading ? (
                <span className="result-head__searching">Searching equipment</span>
              ) : results.isSuccess ? (
                <>
                  <span className="result-head__figure numeric">{results.data.data.length}</span>
                  <span className="result-head__unit">
                    {results.data.page.has_more ? 'or more ' : ''}published{' '}
                    {results.data.data.length === 1 ? 'machine' : 'machines'}
                    {selectedCategory === undefined ? '' : ` in ${selectedCategory.name}`}
                  </span>
                </>
              ) : null}
            </p>
            {/* A count, not a second Clear control: the filter panel already
                owns that action, and two buttons with one name is a screen
                reader hearing the same command twice. */}
            {hasFilters ? (
              <p className="result-head__applied">
                {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} applied
              </p>
            ) : null}
          </div>

          {results.isLoading ? <EquipmentGridSkeleton /> : null}

          {results.isError ? (
            <ErrorState error={results.error} onRetry={() => void results.refetch()} />
          ) : null}

          {results.isSuccess && results.data.data.length === 0 ? (
            hasFilters ? (
              <EmptyState
                title="No machines match these filters"
                body="Try widening the category, or clearing the dates to see what is published."
                action={
                  <Button variant="secondary" onClick={() => setParams(new URLSearchParams())}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No equipment is published yet"
                body="Machines appear here once an owner lists and publishes them."
              />
            )
          ) : null}

          {results.isSuccess && results.data.data.length > 0 ? (
            <>
              <EquipmentGrid
                units={results.data.data}
                modelsById={modelsById}
                taxonomyById={taxonomyById}
                matchedPeriod={searchedWithDates}
              />

              <Pagination
                hasPrevious={cursor !== null}
                hasNext={results.data.page.has_more && results.data.page.next_cursor !== null}
                onPrevious={() => navigate(-1)}
                onNext={() => goToNextPage(results.data.page.next_cursor as string)}
                busy={results.isFetching}
              />
            </>
          ) : null}
        </section>

        {/*
          Filters follow the machines in the document. On a phone that puts them
          where a secondary control belongs — under the results, reachable but
          not the first impression. From `lg` the grid lifts them back up beside
          the results, where a wide screen has room for both at once.
        */}
        <aside className="search-page__filters" aria-label="Filters">
          <EquipmentFilters
            values={values}
            taxonomy={taxonomy.data ?? []}
            models={models.data ?? []}
            attributes={attributes.data ?? []}
            loading={taxonomy.isLoading || models.isLoading}
            onChange={applyFilters}
            onClear={() => setParams(new URLSearchParams())}
          />

          {/*
            The backend states `geospatial: 'unsupported'` on every response
            (BLK-4). Distance search is a whole capability that does not exist,
            so it gets the component the 58 blocked endpoints get — not a
            disabled radius slider, which would imply it is merely switched off.
            It sits here, beside the controls, because somebody choosing how to
            narrow results is the person it concerns.
          */}
          {results.data?.search_meta.geospatial === 'unsupported' ? (
            <div className="search-page__blocked">
              <BlockedState
                compact
                capability="Search by distance"
                reason="These results are not filtered by where a machine is, and no distance, radius or “nearest” ordering is applied to them. Location is arranged with the owner when you agree terms."
              />
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
